
import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState, memo, useRef } from "react";
import "./Timeline.scss";
import TimelineToggleButton from "./TimelineToggleButton";
import { generateTimeIntervals, generateEpochIntervals, calculateFrameSize, onTimelineControl } from "./utils";
import CustomToolTip from "../CustomToolTip";
import ResizableRangeSlider from "../draggable-resizable-slider/ResizableRangeSlider";
import Skeleton from "../skeleton/Skeleton";
import { ComponentProps, ExposerProps, FaultIndicatorProps, OrbitIndicatorProps, RealtimeTrackerProps, TimelineContainerProps, TimelineContrillerProps, TimelineDragSliderProps, TimelineEventProps, TimelineProps, TimelineLayoutContextType, TimelineDataContextType, EventProps } from "./types";
import { SpeedoMeterSvg, TriangleIndicatorSvg } from "../svgs/Svgs";
import useWindow from "./hooks/useWindow";
import { get_formated_time, getFormatedTime, mergeClassNames } from "../../../utils/commonFunctions";
import CustomSlider from "../custom-slider/CustomSlider";
import { useRealtimeTrackerVisibility } from "./hooks/useRealtimeTrackerVisibility";
import { NextSvg, PlayPauseSvg, PreviousSvg } from "./Svg";
import { OverlayTrigger } from "react-bootstrap";
import usePopover from "../popover/usePopover";

/* ----------------------------
   Context + Hook
   ---------------------------- */
const TimelineLayoutContext = createContext<TimelineLayoutContextType | undefined>(undefined);
const TimelineDataContext = createContext<TimelineDataContextType | undefined>(undefined);

const useTimelineLayout = (): TimelineLayoutContextType => {
    const ctx = useContext(TimelineLayoutContext);
    if (!ctx) throw new Error("All Timeline layout subcomponents must be inside Timeline");
    return ctx;
};

const useTimelineData = (): TimelineDataContextType => {
    const ctx = useContext(TimelineDataContext);
    if (!ctx) throw new Error("All Timeline data subcomponents must be inside Timeline");
    return ctx;
};

// Backwards-compatible merged hook (keeps existing API)
export const useTimeline = () => {
    const layout = useTimelineLayout();
    const data = useTimelineData();
    return { ...layout, ...data } as any;
};


/* ----------------------------
   Main Timeline Component
   ---------------------------- */
export const Timeline: React.FC<TimelineProps & React.HTMLAttributes<HTMLDivElement>> = ({
    active: activeProp,
    show = true,
    loading,
    children,
    hideToggleButton,
    onToggle,
    toggleIcon,
    disabled,
    disabledToggleButton,
    className = '',
    timelineStartTime,
    timelineEndTime,
    timelineDuration,
    showSidebar = true,
    sidebarSize = 28,
    timelineOffset = 10,
    selectedFrame = 30,
    scrollAreaRef,
    ...rest
}) => {

    const windowWidth = useWindow()
    const internalScrollAreaRef = useRef<HTMLDivElement>(null);
    const effectiveScrollAreaRef = scrollAreaRef ?? internalScrollAreaRef;
    const realTimeTrackerIndicatorRef = React.useRef<HTMLDivElement>(null);

    const [active, setActive] = useState<boolean>(activeProp ?? true);

    useEffect(() => {
        if (activeProp !== undefined) {
            setActive(activeProp);
        }
    }, [activeProp]);

    const [visibleRange, setVisibleRange] = useState<{ start: number; end: number }>({ start: 0, end: 0 });
    const [scrollContainerWidth, setScrollContainerWidth] = useState<number>(0);

    const updateVisibleRange = useCallback(() => {
        const container = effectiveScrollAreaRef.current;
        if (!container) return;

        setVisibleRange({
            start: container.scrollLeft,
            end: container.scrollLeft + container.clientWidth
        });
    }, [effectiveScrollAreaRef]);

    useLayoutEffect(() => {
        const el = effectiveScrollAreaRef.current;
        if (!el) return;

        const updateWidth = () => {
            setScrollContainerWidth(el.clientWidth);
            updateVisibleRange();
        };
        updateWidth();

        if (typeof ResizeObserver !== 'undefined') {
            const ro = new ResizeObserver(() => updateWidth());
            ro.observe(el);
            return () => ro.disconnect();
        }

        window.addEventListener('resize', updateWidth);
        return () => window.removeEventListener('resize', updateWidth);
    }, [effectiveScrollAreaRef, updateVisibleRange]);

    const availableWidth = useMemo(() => (scrollContainerWidth > 0 ? scrollContainerWidth : windowWidth), [scrollContainerWidth, windowWidth]);
    const timeFrameWidth = useMemo(() => availableWidth - timelineOffset - sidebarSize, [availableWidth, sidebarSize, timelineOffset]);
    const { frameSize, frameOffset, frameInterval, pixelInSec } = useMemo(() => calculateFrameSize(selectedFrame, timeFrameWidth), [selectedFrame, timeFrameWidth]);
    const Frames: number[] = useMemo(() => generateEpochIntervals(timelineStartTime, timelineEndTime, frameOffset), [timelineStartTime, timelineEndTime, frameOffset]);
    const Intervals: number[] = useMemo(() => generateTimeIntervals(timelineDuration, frameInterval), [timelineDuration, frameInterval]);

    const TimelineWidth = useMemo(() => Frames.length > 0 ? (Frames.length - 1) * frameSize : 0, [frameSize, Frames]);

    // stable function to compute left/width for an event
    const getFrameStyle = useCallback((startTime: number, endTime: number, offcet: number = 0, responseType: 'string' | 'number' = 'string') => {
        const safeStartTime = Number.isFinite(startTime) ? startTime : timelineStartTime;
        const safeEndTime = Number.isFinite(endTime) ? endTime : safeStartTime;
        const leftPosition = (safeStartTime - timelineStartTime) * pixelInSec;
        const width = Math.max((safeEndTime - safeStartTime) * pixelInSec, 0);
        return { left: responseType === 'string' ? `${leftPosition - offcet}px` : (leftPosition - offcet), width: responseType === 'string' ? `${width}px` : width };
    }, [timelineStartTime, pixelInSec]);

    useEffect(() => {
        const container = effectiveScrollAreaRef.current;
        if (!container) return;
        updateVisibleRange();
        container.addEventListener('scroll', updateVisibleRange);

        return () => {
            container.removeEventListener('scroll', updateVisibleRange);
        };
    }, [effectiveScrollAreaRef, updateVisibleRange]);


    const handleToggle = useCallback(() => {
        setActive((prev) => {
            const next = !prev;
            onToggle?.(next);
            return next;
        });
    }, [onToggle]);

    const dataValue = useMemo(() => ({ Frames, Intervals, loading: !!loading }), [Frames, Intervals, loading]);

    const layoutValue = useMemo(() => ({
        visibleRange,
        sidebarSize,
        frameSize,
        frameInterval,
        timelineStartTime,
        timelineEndTime,
        timelineDuration,
        getFrameStyle,
        TimelineWidth,
        scrollAreaRef: effectiveScrollAreaRef,
        realTimeTrackerIndicatorRef,
    }), [sidebarSize, frameSize, frameInterval, timelineStartTime, timelineEndTime, timelineDuration, getFrameStyle, TimelineWidth, visibleRange, effectiveScrollAreaRef]);

    return (
        <TimelineLayoutContext.Provider value={layoutValue}>
            <TimelineDataContext.Provider value={dataValue}>
                <div className={`timeline__container__wrapper ${showSidebar && "show__sidebar"} ${(active && show) && "active"} ${disabled && "disabled"}`} {...rest}>
                    <TimelineToggleButton active={active} onToggle={handleToggle} hideToggle={hideToggleButton} children={toggleIcon} disabled={disabled || disabledToggleButton || !show} />
                    <div className={mergeClassNames('timeline__wrapper', className)}>
                        {children}
                    </div>
                </div>
            </TimelineDataContext.Provider>
        </TimelineLayoutContext.Provider>
    );
};


export const TimelineHeader: React.FC<ComponentProps> = ({ children, className = '', ...rest }) => {
    return <div className={mergeClassNames('timeline__header__container', className)} {...rest}>{children}</div>;
};


const TimelineSkeleton: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ className = '', ...rest }) => {
    return (
        <div className={mergeClassNames('timeline__skeleton__container', className)} {...rest}>
            <Skeleton style={{ height: '35px' }} />
            <Skeleton style={{ height: '50px' }} />
        </div>
    );
}

export const TimelineBodyContainer: React.FC<ComponentProps> = ({ children, className = '', ...rest }) => {
    const { loading } = useTimeline();
    return (
        <div className={mergeClassNames('timeline__body__container', className)} {...rest}>
            {loading && <TimelineSkeleton />}
            {children}
        </div>
    );
};


export const TimelineScrollContainer: React.FC<ComponentProps> = ({ children, className = '', ...rest }) => {
    const { scrollAreaRef } = useTimeline();
    return (
        <div ref={scrollAreaRef} className={mergeClassNames('timeline__scroll__container', className)} {...rest}>
            {children}
        </div>
    );
};



export const TimelineContainer: React.FC<TimelineContainerProps> = ({ children, sidebarDetails, className }) => {
    const { TimelineWidth, sidebarSize } = useTimeline();
    const containerWidth = useMemo(() => `${TimelineWidth + sidebarSize}px`, [TimelineWidth, sidebarSize]);

    const asChild = sidebarDetails?.asChild && sidebarDetails?.children
    return (
        <div className={mergeClassNames(`timeline___container`, className)} style={{ width: containerWidth }}>
            {!asChild &&
                <div className={mergeClassNames(`timeline__sidebar`, sidebarDetails?.className)} style={{ width: `${sidebarSize}px` }}>
                    {sidebarDetails?.icon &&
                        <div className={`timeline__sidebar__icon`}>
                            <CustomToolTip placement="right" title={sidebarDetails?.name || ''}>
                                {sidebarDetails?.icon}
                            </CustomToolTip>
                        </div>
                    }
                    {!sidebarDetails?.icon &&
                        <div className='timeline__sidebar__name__container'>
                            <span className='timeline__sidebar__name'>
                                <CustomToolTip placement="right" title={sidebarDetails?.name || ''}>
                                    {sidebarDetails?.name}
                                </CustomToolTip>
                            </span>
                        </div>
                    }
                </div>
            }
            {asChild && <div className={mergeClassNames(`timeline__sidebar`, sidebarDetails?.className)} style={{ width: `${sidebarSize}px` }}> {sidebarDetails?.children}</div>}
            {children}
        </div>
    );
};


export const TimelineTrackContainer: React.FC<ComponentProps> = ({ children, className = '', ...rest }) => {
    const { TimelineWidth } = useTimeline();
    return (
        <div className={mergeClassNames('timeline__track__container', className)} style={{ width: `${TimelineWidth}px` }} {...rest}>
            {children}
        </div>
    );
};


export const TimelineTrack: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ className, ...rest }) => {
    const { Frames, frameSize, timelineStartTime, Intervals, visibleRange } = useTimeline();

    return (
        <div className={mergeClassNames(`timeline__track`, className)} {...rest}>
            {Frames?.length > 0 && Frames.map((time: number, index: number) => {
                const value = (time - timelineStartTime) / 60;
                const interval = Intervals.includes(value);
                const width = index === Frames.length - 1 ? "0px" : `${frameSize}px`;
                const formatedTime = `${get_formated_time(time)}`;
                const left = index * frameSize;
                const right = left + frameSize;
                if (right < visibleRange.start || left > visibleRange.end) return null;
                return (
                    <div className={`track__interval ${interval ? "highlight__interval" : ""}`} style={{
                        width: width,
                        position: "absolute",
                        left: left
                    }} key={time}>
                        <div className="track__interval__time">
                            <CustomToolTip title={formatedTime}>{value}</CustomToolTip>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};


export const ExposerContainer: React.FC<ComponentProps> = ({ children, className = '', ...rest }) => {
    return <div className={mergeClassNames('exposer__container', className)} {...rest}>{children}</div>;
}


export const Exposer: React.FC<ExposerProps> = ({ exposerEndTime, exposerStartTime, exposerType, className = '', ...rest }) => {
    const { getFrameStyle, visibleRange } = useTimeline();
    const { left, width } = useMemo(() => getFrameStyle(exposerStartTime, exposerEndTime, 0, 'number'), [getFrameStyle, exposerStartTime, exposerEndTime]);
    const showExposer = useMemo(() => (left + width) >= visibleRange.start && left <= visibleRange.end, [visibleRange, left, width]);
    const exposer = exposerType === 'Sunlit' ? 'sunlit__exposer' : 'eclipse__exposer'
    if (!showExposer) return null
    return (
        <div className={mergeClassNames('exposer', exposer, className)} {...rest}
            style={{ width: `${width}px`, left: `${left}px` }}
        />
    );
}


export const OrbitIndicatorsContainer: React.FC<ComponentProps> = ({ children, className = '', ...rest }) => {
    return <div className={mergeClassNames('orbit__indicators__container', className)} {...rest}>{children}</div>;
};


export const OrbitIndicator: React.FC<OrbitIndicatorProps> = ({ className = '', orbitName, orbitStartTime, orbitEndTime, uniqueId, indicatorSize = 8, ...rest }) => {
    const { getFrameStyle } = useTimeline();
    const [mouseEnter, setMouseEnter] = useState<boolean>(false);

    const styleWidth = useMemo(() => {
        return mouseEnter ? getFrameStyle(orbitStartTime, orbitEndTime).width : `${indicatorSize}px`;
    }, [mouseEnter, getFrameStyle, orbitStartTime, orbitEndTime, indicatorSize]);

    const { left } = useMemo(() => getFrameStyle(orbitStartTime, orbitEndTime, 0, 'number'), [getFrameStyle, orbitStartTime, orbitEndTime]);
    return (
        <div
            className={mergeClassNames('orbit__indicator', className)}
            {...rest}
            id={uniqueId}
            onMouseEnter={() => setMouseEnter(true)}
            onMouseLeave={() => setMouseEnter(false)}
            style={{
                width: styleWidth,
                left: `${left}px`,
            }}
        >
            <OverlayTrigger trigger={['hover', 'focus']} placement="top" overlay={
                <div className='exposer__overlay__container'>
                    <div className='exposer__overlay__container__details__section'>
                        <div className='details__label__container ps-1 pt-1'>{orbitName}</div>
                    </div>
                    <div className='exposer__overlay__container__details__section'>
                        <div className='details__icon__container'>ST</div>
                        <div className='details__label__container'>{get_formated_time(orbitStartTime)}</div>
                    </div>
                    <div className='exposer__overlay__container__details__section'>
                        <div className='details__icon__container'>ET</div>
                        <div className='details__label__container'>{get_formated_time(orbitEndTime)}</div>
                    </div>
                </div>
            }>
                <div className="orbit__details__container" />
            </OverlayTrigger>
        </div>
    );
};


export const TimelineFaultsContainer: React.FC<ComponentProps> = ({ children, className = '', ...rest }) => {
    return <div className={mergeClassNames('timeline__faults__container', className)} {...rest}>{children}</div>;
}


export const FaultIndicator: React.FC<FaultIndicatorProps> = ({ className = '', indicatorRef, faultStartTime, faultEndTime, count, ...rest }) => {
    const { getFrameStyle, visibleRange } = useTimeline();
    const { left, width } = useMemo(() => getFrameStyle(faultStartTime, faultEndTime, 10, 'number'), [getFrameStyle, faultStartTime, faultEndTime]);
    const showFault = useMemo(() => (left + width) >= (visibleRange.start - 10) && left <= visibleRange.end, [visibleRange, left, width]);

    if (!showFault) return null
    return (
        <div ref={indicatorRef} className={mergeClassNames('scenario__fault__indicator__container', className)} style={{ left: `${left}px` }} {...rest}>
            <TriangleIndicatorSvg num={count} />
        </div>
    );
}


export const TimelineEventTrack: React.FC<ComponentProps> = ({ children, className = '', ...rest }) => {
    return (
        <TimelineTrackContainer>
            <div className={mergeClassNames('timeline__event__track', className)} {...rest}>{children}</div>
        </TimelineTrackContainer>
    );
};


export const TimelineEvent: React.FC<TimelineEventProps> = ({ children, className = '', eventStart, eventEnd, ...rest }) => {
    const { getFrameStyle, visibleRange } = useTimeline();
    const { left, width } = useMemo(() => getFrameStyle(eventStart, eventEnd, 0, 'number'), [getFrameStyle, eventStart, eventEnd]);
    const showEvent = useMemo(() => (left + width) >= visibleRange.start && left <= visibleRange.end, [visibleRange, left, width]);
    if (!showEvent) return null
    return (
        <div className={mergeClassNames('timeline__event', className)} style={{ width: `${width}px`, left: `${left}px` }} {...rest}>
            {children}
        </div>
    );
};


export const TimelineContainerDevider: React.FC<ComponentProps> = ({ className = '', ...rest }) => {
    return <div className={mergeClassNames('timeline__container__devider', className)} {...rest} />;
};


export const TimelineDragSlider: React.FC<TimelineDragSliderProps> = ({ className = '', value, disabled, onChange }) => {
    const { timelineStartTime, timelineEndTime } = useTimeline();
    return (
        <div className={mergeClassNames('resizable__container', className)}>
            <ResizableRangeSlider disabled={disabled ? true : false} draggable value={value} minValue={timelineStartTime / 60} maxValue={timelineEndTime / 60} onChange={onChange} />
        </div>
    );
};


export const RealtimeTracker: React.FC<RealtimeTrackerProps> = ({ className = '', currentTime, strtTime, endTime, duration, trackerStep = 1, disabled, onChange }) => {

    const { getFrameStyle, realTimeTrackerIndicatorRef } = useTimeline();
    const { left, width } = useMemo(() => getFrameStyle(strtTime, endTime, 0, 'number'), [getFrameStyle, strtTime, endTime]);
    return (
        <div className={mergeClassNames('realtime__tracker__container', className)}>
            <div style={{
                position: 'absolute',
                left: left,
                width: width,
                zIndex: 2
            }}>
                <div className="point__indicator left">
                    <CustomToolTip title={getFormatedTime(strtTime)}>
                        <div className="time__stamp" />
                    </CustomToolTip>
                </div>
                <CustomSlider
                    min={strtTime}
                    max={endTime}
                    width={width as number}
                    trackHeight={1}
                    thumbSize={12}
                    content={getFormatedTime(currentTime)}
                    value={currentTime}
                    step={trackerStep}
                    disabled={disabled}
                    onChange={onChange}
                    thumbRef={realTimeTrackerIndicatorRef}
                    thumbClassName="realtime__tracker__thumb"
                />
                <div className="point__indicator right">
                    <CustomToolTip title={getFormatedTime(endTime)}>
                        <div className="time__stamp" />
                    </CustomToolTip>
                </div>
            </div>
        </div>
    )
}



export const SpeedoMeter: React.FC<{ value: number, className?: string }> = ({ value, className = '' }) => {
    return (
        <div className={mergeClassNames('speedometer__container', className)}>
            <SpeedoMeterSvg />
            <div className="speedometer__value">
                {value}<span className="speedometer__tag">x</span>
            </div>
        </div>
    );
}


export const LiveButton: React.FC<ComponentProps & { onClick: () => void }> = memo(({ className = '', onClick }) => {
    const { realTimeTrackerIndicatorRef, scrollAreaRef, timelineStartTime, Frames } = useTimeline();

    const autoScorll = useMemo(() => Frames?.length > 0 && timelineStartTime, [Frames?.length, timelineStartTime])
    // Professional hook for visibility and scroll management
    const {
        isVisible,
        isUserScrolling,
        scrollToIndicator,
    } = useRealtimeTrackerVisibility({
        indicatorRef: realTimeTrackerIndicatorRef,
        scrollContainerRef: scrollAreaRef,
        scrollBehavior: 'smooth',
        smoothScrollDuration: 400,
        intersectionThreshold: 0,
        scrollDebounceMs: 1000,
        autoScrollOnMount: true, // Auto-scroll to indicator on first load
        autoScorll: autoScorll
    });

    const onButtonClick = () => {
        scrollToIndicator()
        onClick()
    }

    return (
        <button
            className={mergeClassNames(
                `live__button__container ${isVisible ? 'live' : ''}`,
                className
            )}
            disabled={isVisible}
            onClick={onButtonClick}
            title={isUserScrolling ? 'Scrolling...' : 'Click to scroll to live indicator'}
            aria-label="Scroll to live indicator"
            aria-pressed={isVisible}
        >
            <span className="live__button__text">Live</span>
            <div className="live__button__indicator" />
        </button>
    );
});



export const TimelineController: React.FC<TimelineContrillerProps> = ({ step, controllerState, onStepChange, onControllerChange, showController = false, className = '' }) => {

    const onControl = useCallback((controlType: 'next' | 'previous' | 'play' | 'pause') => {
        const data = onTimelineControl(controlType, step)
        if (typeof data === 'number') {
            onStepChange(data)
        } else {
            onControllerChange(data as 'play' | 'pause')
        }
    }, [onStepChange, onControllerChange, step])

    const handlePrevious = useCallback(() => onControl('previous'), [onControl])
    const handlePlayPause = useCallback(() => onControl(controllerState), [onControl, controllerState])
    const handleNext = useCallback(() => onControl('next'), [onControl])
    if (!showController) return null
    return (
        <div className={mergeClassNames("timeline__controller__container", className)}>
            <div className='controller__buttons__container'>
                <button className='controller__previous__button' onClick={handlePrevious}>
                    <PreviousSvg active={step <= -32 ? true : false} />
                    <PreviousSvg active={step <= -8 ? true : false} />
                    <PreviousSvg active={step <= -2 ? true : false} />
                </button>
                <button className='controller__play__pause__buton' onClick={handlePlayPause}>
                    <PlayPauseSvg state={controllerState} />
                </button>
                <button className='controller__next__button' onClick={handleNext}>
                    <NextSvg active={step >= 2 ? true : false} />
                    <NextSvg active={step >= 8 ? true : false} />
                    <NextSvg active={step >= 32 ? true : false} />
                </button>
            </div>
        </div>
    )
}


export const Event: React.FC<EventProps> = ({ startTime, endTime, eventName, icon, statusIcon, allEvents, overlay: Overlay, eventType, className = '', onClick }) => {

    const elRef = useRef<HTMLDivElement | null>(null);
    const { showPopover } = usePopover();

    const isTimeOverlap = (aStart: number, aEnd: number, bStart: number, bEnd: number) => Math.max(aStart, bStart) <= Math.min(aEnd, bEnd);
    const handleMouseEnter = (e: React.MouseEvent) => {
        if (!elRef.current || !Overlay) return;
        const overlapped = allEvents.filter((ev) => isTimeOverlap(startTime, endTime, ev.startTime, ev.endTime));
        showPopover(elRef.current, (<Overlay Events={overlapped} />), { x: e.clientX, y: e.clientY });
    };



    return (
        <TimelineEvent eventStart={startTime} eventEnd={endTime}>
            <div
                ref={elRef}
                onMouseEnter={handleMouseEnter}
                onClick={onClick}
                className={mergeClassNames(`event__container`, className, eventType)}
            >
                {statusIcon && <div className='event__status__icon'>{statusIcon}</div>}
                {icon && <div className='event__icon'>{icon}</div>}
                <span className='event__text'>{eventName}</span>
            </div>
        </TimelineEvent>
    )
}
